import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

interface BackendHealthStatus {
  isConnected: boolean;
  isChecking: boolean;
  error: Error | null;
}

const HEALTH_LIVE_PATH = '/health/live';
const INTERVAL_WHEN_CONNECTED_MS = 60_000;
const INTERVAL_WHEN_DISCONNECTED_MS = 15_000;
const INTERVAL_AFTER_RATE_LIMIT_MS = 120_000;
const REQUEST_TIMEOUT_MS = 5_000;

const getApiBaseUrl = () => process.env.REACT_APP_API_URL ?? 'http://localhost:5019/api';

const getHealthBaseUrl = () => getApiBaseUrl().replace(/\/api\/?$/, '');

const isRateLimited = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 429;

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Backend unreachable');

export const useBackendHealth = () => {
  const [status, setStatus] = useState<BackendHealthStatus>({
    isConnected: true,
    isChecking: true,
    error: null,
  });

  const inFlightRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isConnectedRef = useRef(true);
  const rateLimitedRef = useRef(false);

  const getNextInterval = () => {
    if (rateLimitedRef.current) return INTERVAL_AFTER_RATE_LIMIT_MS;
    return isConnectedRef.current ? INTERVAL_WHEN_CONNECTED_MS : INTERVAL_WHEN_DISCONNECTED_MS;
  };

  const scheduleNextRef = useRef<(delayMs?: number) => void>(() => {});

  const runCheck = useCallback(async () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    const healthBaseUrl = getHealthBaseUrl();
    const apiBaseUrl = getApiBaseUrl();

    try {
      await axios.get(`${healthBaseUrl}${HEALTH_LIVE_PATH}`, {
        timeout: REQUEST_TIMEOUT_MS,
        validateStatus: (s) => s < 500,
      });
      rateLimitedRef.current = false;
      isConnectedRef.current = true;
      setStatus({ isConnected: true, isChecking: false, error: null });
    } catch (error) {
      if (isRateLimited(error)) {
        rateLimitedRef.current = true;
        setStatus((prev) => ({ ...prev, isChecking: false }));
        return;
      }

      try {
        await axios.get(apiBaseUrl, {
          timeout: REQUEST_TIMEOUT_MS,
          validateStatus: (s) => s < 500,
        });
        rateLimitedRef.current = false;
        isConnectedRef.current = true;
        setStatus({ isConnected: true, isChecking: false, error: null });
      } catch (fallbackError) {
        if (isRateLimited(fallbackError)) {
          rateLimitedRef.current = true;
          setStatus((prev) => ({ ...prev, isChecking: false }));
          return;
        }

        rateLimitedRef.current = false;
        isConnectedRef.current = false;
        setStatus({
          isConnected: false,
          isChecking: false,
          error: toError(error),
        });
      }
    } finally {
      inFlightRef.current = false;
      scheduleNextRef.current();
    }
  }, []);

  scheduleNextRef.current = (delayMs = getNextInterval()) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      void runCheck();
    }, delayMs);
  };

  const retry = useCallback(() => {
    rateLimitedRef.current = false;
    setStatus((prev) => ({ ...prev, isChecking: true }));
    clearTimeout(timeoutRef.current);
    void runCheck();
  }, [runCheck]);

  useEffect(() => {
    void runCheck();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timeoutRef.current);
        void runCheck();
      } else {
        clearTimeout(timeoutRef.current);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeoutRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [runCheck]);

  return {
    ...status,
    retry,
  };
};
